import React, { useEffect, useState } from "react";
import { Landmark, ShieldPlus, Receipt } from "lucide-react";
import { pfSettingsApi, PfSettings as PfSettingsT } from "../../api/pfSettings";
import { incomeTaxSettingsApi, IncomeTaxSettings as IncomeTaxSettingsT } from "../../api/incomeTaxSettings";
import { useToast } from "../../hooks/useToast";

export default function PfEsicSettings() {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PfSettingsT | null>(null);

  const [establishmentId, setEstablishmentId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [esicUsername, setEsicUsername] = useState("");
  const [esicPassword, setEsicPassword] = useState("");

  const [itSettings, setItSettings] = useState<IncomeTaxSettingsT | null>(null);
  const [itUsername, setItUsername] = useState("");
  const [itPassword, setItPassword] = useState("");
  const [itSaving, setItSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await pfSettingsApi.get();
        if (cancelled) return;
        const s = r.data?.settings || null;
        setSettings(s);
        if (s) {
          setEstablishmentId(s.establishment_id || "");
          setUsername(s.username || "");
          setEsicUsername(s.esic_username || "");
        }
      } catch (e: any) {
        toast({
          variant: "destructive",
          description: e?.response?.data?.detail || "Failed to load PF settings",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    (async () => {
      try {
        const r = await incomeTaxSettingsApi.get();
        if (cancelled) return;
        const s = r.data?.settings || null;
        setItSettings(s);
        if (s) setItUsername(s.username || "");
      } catch (e: any) {
        if (!cancelled) {
          toast({
            variant: "destructive",
            description: e?.response?.data?.detail || "Failed to load Income Tax e-filing settings",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!username.trim()) {
      toast({ variant: "destructive", description: "EPFO username is required." });
      return;
    }
    if (!settings?.has_password && !password.trim()) {
      toast({ variant: "destructive", description: "Password is required for first-time setup." });
      return;
    }

    setSaving(true);
    try {
      const payload: Parameters<typeof pfSettingsApi.save>[0] = {
        establishment_id: establishmentId.trim(),
        username: username.trim(),
        esic_username: esicUsername.trim(),
      };
      if (password.trim()) payload.password = password.trim();
      if (esicPassword.trim()) payload.esic_password = esicPassword.trim();

      const r = await pfSettingsApi.save(payload);
      const s: PfSettingsT = r.data?.settings;
      if (s) setSettings(s);
      setPassword("");
      setEsicPassword("");
      toast({ description: "PF/ESIC settings saved." });
    } catch (e: any) {
      toast({
        variant: "destructive",
        description: e?.response?.data?.detail || "Failed to save PF settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIt = async () => {
    if (!itUsername.trim()) {
      toast({ variant: "destructive", description: "Income Tax e-filing username (PAN) is required." });
      return;
    }
    if (!itSettings?.has_password && !itPassword.trim()) {
      toast({ variant: "destructive", description: "Password is required for first-time setup." });
      return;
    }

    setItSaving(true);
    try {
      const payload: Parameters<typeof incomeTaxSettingsApi.save>[0] = { username: itUsername.trim() };
      if (itPassword.trim()) payload.password = itPassword.trim();

      const r = await incomeTaxSettingsApi.save(payload);
      const s: IncomeTaxSettingsT = r.data?.settings;
      if (s) setItSettings(s);
      setItPassword("");
      toast({ description: "Income Tax e-filing settings saved." });
    } catch (e: any) {
      toast({
        variant: "destructive",
        description: e?.response?.data?.detail || "Failed to save Income Tax e-filing settings",
      });
    } finally {
      setItSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="smset-scope">
        <Style />
        <div className="smset-wrap">
          <div className="smset-meta" style={{ padding: 28 }}>Loading PF settings…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="smset-scope">
      <Style />
      <div className="smset-wrap">
        <header className="smset-rise" style={{ animationDelay: "0ms" }}>
          <div className="smset-eyebrow">Workspace · Compliance</div>
          <h1 className="smset-display">PF/ESIC Settings</h1>
          <p className="smset-sub">
            EPFO and ESIC employer-portal logins the Startup Khata extension uses to file
            on this branch's behalf. You still solve the CAPTCHA yourself. ESIC filing
            automation is coming next — for now this just stores the login.
          </p>
        </header>

        <section className="smset-card smset-rise" style={{ animationDelay: "90ms" }}>
          <div className="smset-chead">
            <span className="smset-badge"><Landmark size={16} /></span>
            <div>
              <div className="smset-eyebrow">EPFO</div>
              <h2 className="smset-ctitle">Employer Portal Login</h2>
              <p className="smset-cdesc">unifiedportal-emp.epfindia.gov.in — establishment login.</p>
            </div>
          </div>

          <div className="smset-fields">
            <div>
              <label className="smset-flabel">Establishment ID</label>
              <input
                type="text"
                value={establishmentId}
                onChange={(e) => setEstablishmentId(e.target.value)}
                placeholder="e.g. DLCPM0012345000"
                className="smset-input"
              />
            </div>

            <div>
              <label className="smset-flabel">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="EPFO portal username"
                className="smset-input"
              />
            </div>

            <div>
              <label className="smset-flabel">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={settings?.has_password ? "Leave blank to keep current password" : "EPFO portal password"}
                className="smset-input"
              />
            </div>
          </div>

          {settings && (
            <p className="smset-cardmeta">
              {settings.has_password ? "Password configured" : "No password saved yet"}
              {settings.updated_at && ` · updated ${new Date(settings.updated_at).toLocaleDateString()}`}
            </p>
          )}
        </section>

        <section className="smset-card smset-rise" style={{ animationDelay: "130ms" }}>
          <div className="smset-chead">
            <span className="smset-badge"><ShieldPlus size={16} /></span>
            <div>
              <div className="smset-eyebrow">ESIC</div>
              <h2 className="smset-ctitle">Employer Portal Login</h2>
              <p className="smset-cdesc">esic.gov.in — just stored for now, filing automation comes later.</p>
            </div>
          </div>

          <div className="smset-fields">
            <div>
              <label className="smset-flabel">Username</label>
              <input
                type="text"
                value={esicUsername}
                onChange={(e) => setEsicUsername(e.target.value)}
                placeholder="ESIC portal username"
                className="smset-input"
              />
            </div>

            <div>
              <label className="smset-flabel">Password</label>
              <input
                type="password"
                value={esicPassword}
                onChange={(e) => setEsicPassword(e.target.value)}
                placeholder={settings?.has_esic_password ? "Leave blank to keep current password" : "ESIC portal password"}
                className="smset-input"
              />
            </div>
          </div>

          {settings && (
            <p className="smset-cardmeta">
              {settings.has_esic_password ? "Password configured" : "No password saved yet"}
            </p>
          )}
        </section>

        <section className="smset-card smset-rise" style={{ animationDelay: "150ms" }}>
          <div className="smset-chead">
            <span className="smset-badge"><Receipt size={16} /></span>
            <div>
              <div className="smset-eyebrow">Income Tax</div>
              <h2 className="smset-ctitle">e-Filing Portal Login</h2>
              <p className="smset-cdesc">eportal.incometax.gov.in — used by "File TDS" on Payroll Reports to fill the challan.</p>
            </div>
          </div>

          <div className="smset-fields">
            <div>
              <label className="smset-flabel">Username (PAN)</label>
              <input
                type="text"
                value={itUsername}
                onChange={(e) => setItUsername(e.target.value)}
                placeholder="e-filing portal username / PAN"
                className="smset-input"
              />
            </div>

            <div>
              <label className="smset-flabel">Password</label>
              <input
                type="password"
                value={itPassword}
                onChange={(e) => setItPassword(e.target.value)}
                placeholder={itSettings?.has_password ? "Leave blank to keep current password" : "e-filing portal password"}
                className="smset-input"
              />
            </div>
          </div>

          <div className="smset-actionbar" style={{ marginTop: 12, position: "static", boxShadow: "none" }}>
            <p className="smset-meta">
              {itSettings?.has_password ? "Password configured" : "No password saved yet"}
              {itSettings?.updated_at && ` · updated ${new Date(itSettings.updated_at).toLocaleDateString()}`}
            </p>
            <button onClick={handleSaveIt} disabled={itSaving} className="smset-btn">
              {itSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </section>

        <div className="smset-actionbar smset-rise" style={{ animationDelay: "170ms" }}>
          <p className="smset-meta">These settings apply to everyone in this branch.</p>
          <button onClick={handleSave} disabled={saving} className="smset-btn smset-btn-lg">
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Scoped styles — reuses the same Teamlio (--gt-*) tokens/classnames as
 *  SmtpSettings.tsx so this page is visually consistent with it. */
function Style() {
  return (
    <style>{`
      .smset-wrap { max-width: 74rem; margin: 0 auto; padding: 0 2px 14px;
        display: flex; flex-direction: column; gap: 12px; }

      @keyframes smset-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
      .smset-rise { animation: smset-rise var(--gt-t-slow) both; }

      .smset-eyebrow { font-size: 10.5px; font-weight: 700; letter-spacing: var(--gt-letter-eyebrow);
        text-transform: uppercase; color: var(--gt-text-tertiary); }
      .smset-display { margin: 3px 0 4px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 500; font-size: clamp(24px, 2.6vw, 30px); line-height: 1.03;
        letter-spacing: -0.01em; color: var(--gt-primary); }
      .smset-sub { margin: 0; color: var(--gt-text-secondary); font-size: 13px; line-height: 1.45; max-width: 70ch; }

      .smset-card { position: relative; background: var(--gt-surface-0); border: 1px solid var(--gt-border);
        border-radius: var(--gt-radius-card); box-shadow: var(--gt-elev-2); padding: 16px 18px; overflow: hidden; }
      .smset-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent); }

      .smset-chead { display: flex; align-items: flex-start; gap: 11px; margin-bottom: 12px; }
      .smset-badge { flex: none; width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center;
        background: var(--gt-primary-container); color: var(--gt-primary); margin-top: 1px; }
      .smset-ctitle { margin: 1px 0 3px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 600; font-size: 18px; line-height: 1.1; color: var(--gt-text-primary); }
      .smset-cdesc { margin: 0; font-size: 12px; line-height: 1.45; color: var(--gt-text-secondary); max-width: 58ch; }

      .smset-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
      @media (max-width: 640px) { .smset-fields { grid-template-columns: 1fr; } }
      .smset-flabel { display: block; margin-bottom: 4px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 500; font-size: 13px; color: var(--gt-text-secondary); }
      .smset-input { width: 100%; box-sizing: border-box; font-family: var(--gt-font-mono) !important; font-size: 13.5px;
        padding: 8px 12px; border-radius: var(--gt-radius-input); background: var(--gt-surface-1);
        border: 1px solid var(--gt-border); color: var(--gt-text-primary);
        transition: border-color var(--gt-t-fast), box-shadow var(--gt-t-fast), background var(--gt-t-fast); }
      .smset-input::placeholder { color: var(--gt-text-disabled); }
      .smset-input:focus { outline: none; border-color: var(--gt-primary); background: var(--gt-surface-0);
        box-shadow: 0 0 0 3px rgba(15, 45, 31, 0.10); }

      .smset-cardmeta { margin: 12px 0 0; padding-top: 10px; border-top: 1px solid var(--gt-divider);
        font-size: 11.5px; color: var(--gt-text-tertiary); }

      .smset-actionbar { position: sticky; bottom: 8px; z-index: 5;
        display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
        margin-top: 2px; padding: 11px 12px 11px 18px; background: var(--gt-surface-0);
        border: 1px solid var(--gt-border); border-radius: 14px; box-shadow: var(--gt-elev-4); }
      .smset-meta { margin: 0; font-size: 12px; color: var(--gt-text-tertiary); }
      .smset-btn-lg { padding: 11px 24px; font-size: 14px; }

      .smset-btn { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; font-size: 14px;
        padding: 10px 20px; border: none; border-radius: 12px; color: #ffffff !important; background: var(--gt-primary);
        cursor: pointer; box-shadow: var(--gt-glow-soft);
        transition: transform var(--gt-t-fast), background var(--gt-t-fast), box-shadow var(--gt-t-fast); }
      .smset-btn:hover:not(:disabled) { background: var(--gt-primary-hover); transform: translateY(-1px); box-shadow: var(--gt-glow); }
      .smset-btn:active:not(:disabled) { transform: translateY(0); }
      .smset-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    `}</style>
  );
}
